import React, { useState } from 'react';
import { createPortal } from 'react-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Round avatar with photo or fallback initials.
 *
 * Props:
 * - photoUrl: relative or absolute URL of the profile photo
 * - firstName, lastName: used to compute initials
 * - size: sm | md | lg | xl (24/32/40/56 px) OR a number in px
 * - onClick: optional click handler (renders as a button-like element)
 * - editable: if true, shows a small camera icon overlay on hover
 * - zoomable: if true (default) and a photo exists (and no onClick), clicking opens
 *   the photo full-size in a centered lightbox (like capture images)
 * - className: extra classes
 * - testId: data-testid prefix
 */
export const Avatar = ({
  photoUrl,
  firstName = '',
  lastName = '',
  size = 'md',
  onClick,
  editable = false,
  zoomable = true,
  className = '',
  testId,
}) => {
  const [zoomOpen, setZoomOpen] = useState(false);
  const sizes = {
    xs: { px: 16, text: 'text-[7px]' },
    sm: { px: 24, text: 'text-[10px]' },
    md: { px: 32, text: 'text-xs' },
    lg: { px: 40, text: 'text-sm' },
    xl: { px: 56, text: 'text-base' },
  };
  const dim = typeof size === 'number'
    ? { px: size, text: size <= 20 ? 'text-[7px]' : size <= 28 ? 'text-[10px]' : size <= 40 ? 'text-xs' : 'text-base' }
    : (sizes[size] || sizes.md);
  const initials = `${(firstName || '').trim().charAt(0)}${(lastName || '').trim().charAt(0)}`.toUpperCase() || '?';
  const colors = ['bg-blue-500', 'bg-emerald-500', 'bg-amber-500', 'bg-rose-500', 'bg-purple-500', 'bg-cyan-500'];
  const colorIdx = (firstName.charCodeAt(0) + lastName.charCodeAt(0)) % colors.length;
  const fallbackColor = colors[colorIdx] || 'bg-gray-400';

  const fullUrl = photoUrl
    ? (photoUrl.startsWith('http') ? photoUrl : `${BACKEND_URL}${photoUrl}`)
    : null;

  const canZoom = !onClick && zoomable && !!fullUrl;
  const handleClick = canZoom
    ? (e) => { e.stopPropagation(); setZoomOpen(true); }
    : onClick;

  const Wrapper = handleClick ? 'button' : 'div';
  const wrapperProps = handleClick
    ? { type: 'button', onClick: handleClick, 'aria-label': editable ? 'Changer ma photo' : `Photo de ${firstName} ${lastName}` }
    : {};

  return (
    <>
      <Wrapper
        {...wrapperProps}
        className={`relative inline-flex items-center justify-center rounded-full overflow-hidden shrink-0 ${handleClick ? 'cursor-pointer hover:ring-2 hover:ring-brand-blue/40 transition' : ''} ${className}`}
        style={{ width: dim.px, height: dim.px, minWidth: dim.px, minHeight: dim.px }}
        data-testid={testId}
      >
        {fullUrl ? (
          <img
            src={fullUrl}
            alt={`${firstName} ${lastName}`}
            className="w-full h-full object-cover"
            loading="lazy"
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />
        ) : (
          <span className={`w-full h-full flex items-center justify-center text-white font-semibold ${dim.text} ${fallbackColor}`}>
            {initials}
          </span>
        )}
        {editable && onClick && (
          <span className="absolute inset-0 bg-black/0 hover:bg-black/30 flex items-center justify-center transition opacity-0 hover:opacity-100">
            <svg className="w-1/2 h-1/2 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </span>
        )}
      </Wrapper>
      {zoomOpen && fullUrl && createPortal(
        <div
          className="fixed inset-0 z-[200] bg-black/75 flex items-center justify-center p-4"
          onClick={(e) => { e.stopPropagation(); setZoomOpen(false); }}
          data-testid="avatar-lightbox"
        >
          <div className="flex flex-col items-center gap-3" onClick={(e) => e.stopPropagation()}>
            <img
              src={fullUrl}
              alt={`${firstName} ${lastName}`}
              className="max-h-[75vh] max-w-[90vw] rounded-2xl shadow-2xl object-contain bg-white"
              data-testid="avatar-lightbox-img"
            />
            <p className="text-white font-semibold text-sm">{firstName} {lastName}</p>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setZoomOpen(false); }}
              className="text-xs font-semibold bg-white/15 hover:bg-white/25 text-white rounded-full px-5 py-2 transition"
              data-testid="avatar-lightbox-close"
            >
              Fermer
            </button>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default Avatar;
